import api from 'api';
import Button from 'components/Button';
import FormActionGroup from 'components/FormActionGroup';
import FormGroup from 'components/FormGroup';
import Modal from 'components/Modal';
import TextField from 'components/TextField';
import {phoneFormat} from 'helpers/phoneFormat';
import {useState} from 'react';
import {Controller, get, useForm} from 'react-hook-form';
import toast from 'react-hot-toast';
import {ClientRole, Customer, SelectOption} from 'types';
import {validateEmail} from 'utils/validators';
import styles from './Customers.module.scss';
import Select from 'components/Select';
import {
  CLIENT_ROLE_LABELS,
  CLIENT_ROLES_OPTIONS,
} from 'constants/select-options';
import Typography from 'components/Typography';
import FeatureGuard from 'components/FeatureGuard';

type Props = {
  isOpen: boolean;
  defaultClientRole?: ClientRole;
  showAdditionalPhoneFields?: boolean;
  onClose: () => void;
  onSuccess: (data: Customer) => void;
  title?: string;
};

interface FormValue {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  phone_2?: string;
  phone_3?: string;
  company: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  zipcode: string;
  state: SelectOption;
  client_role: SelectOption<ClientRole>;
}

function NewCustomerModal({
  isOpen,
  onClose,
  onSuccess,
  defaultClientRole,
  showAdditionalPhoneFields = false,
  title = 'Add New Customer',
}: Props) {
  const defaultClientRoleOption = defaultClientRole
    ? {
        label: CLIENT_ROLE_LABELS[defaultClientRole],
        value: defaultClientRole,
      }
    : undefined;
  const {
    handleSubmit,
    register,
    formState: {errors},
    reset,
    control,
  } = useForm<FormValue>({
    defaultValues: {
      client_role: defaultClientRoleOption,
    },
  });
  const [isLoading, setIsLoading] = useState(false);

  const submithandler = async (data: FormValue) => {
    const {phone_2, phone_3, company, ...rest} = data;
    const user = {
      ...rest,
      phone: data.phone.replace(/\D/g, ''),
      client_role: rest.client_role.value,
    };
    if (data?.phone_2 && data.phone_2.trim() !== '')
      (user as any).phone_2 = phone_2?.replace(/\D/g, '');
    if (data?.phone_3 && data.phone_3.trim() !== '')
      (user as any).phone_3 = phone_3?.replace(/\D/g, '');
    if (data?.company && data.company.trim() !== '')
      (user as any).customer_company = company;
    try {
      setIsLoading(true);
      const data = await api.post('/customers/', user);
      onSuccess(data.data);
      onClose();
      reset();
    } catch (error) {
      toast.error(
        get(error, 'response.data.error_message') ||
          JSON.stringify(get(error, 'response.data', {}) || '') ||
          'Something went wrong'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    reset({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      phone_2: '',
      phone_3: '',
      company: '',
    });
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      onRequestClose={handleClose}
      className={styles.list_wrapper}
    >
      <Typography variant="h2">{title || 'Add New Customer'}</Typography>
      <div>
        <FormGroup columns={1}>
          <TextField
            label="Company"
            placeholder="Company"
            {...register('company', {
              required: defaultClientRole === 'vendors' ? 'Required' : false,
            })}
            error={errors.company?.message}
          />
        </FormGroup>
        <FormGroup columns={2}>
          <TextField
            label="First Name"
            placeholder="First Name"
            {...register('first_name', {required: 'Required'})}
            error={errors.first_name?.message}
          />
          <TextField
            label="Last Name"
            placeholder="Last Name"
            {...register('last_name', {required: 'Required'})}
            error={errors.last_name?.message}
          />
        </FormGroup>

        <FormGroup columns={1}>
          <TextField
            label="Email Address"
            placeholder="Email Address"
            {...register('email', {
              validate: validateEmail,
            })}
            error={errors.email?.message}
          />
        </FormGroup>

        <FormGroup columns={1}>
          <TextField
            label="Phone 1 (main)"
            placeholder="(123) 456-7890"
            type="tel"
            {...register('phone', {
              required: 'Required',
              minLength: {
                value: 14,
                message: 'Enter a valid phone number',
              },
            })}
            onChange={(e) => {
              let input = e.target.value.replace(/\D/g, ''); // present only numbers
              e.target.value = phoneFormat(input);
              // e.target.value = e.target.value.replace(/[^0-9]/g, '');
            }}
            maxLength={14}
            error={errors.phone?.message}
          />
        </FormGroup>
        {showAdditionalPhoneFields && (
          <>
            <FormGroup columns={1}>
              <TextField
                label="Phone 2"
                placeholder="(123) 456-7890"
                type="tel"
                {...register('phone_2', {
                  minLength: {
                    value: 14,
                    message: 'Enter a valid phone number',
                  },
                })}
                onChange={(e) => {
                  let input = e.target.value.replace(/\D/g, ''); // present only numbers
                  e.target.value = phoneFormat(input);
                  // e.target.value = e.target.value.replace(/[^0-9]/g, '');
                }}
                maxLength={14}
                error={errors.phone_2?.message}
              />
            </FormGroup>
            <FormGroup columns={1}>
              <TextField
                label="Phone 3"
                placeholder="(123) 456-7890"
                type="tel"
                {...register('phone_3', {
                  minLength: {
                    value: 14,
                    message: 'Enter a valid phone number',
                  },
                })}
                onChange={(e) => {
                  let input = e.target.value.replace(/\D/g, ''); // present only numbers
                  e.target.value = phoneFormat(input);
                }}
                maxLength={14}
                error={errors.phone_3?.message}
              />
            </FormGroup>
          </>
        )}

        <FeatureGuard feature="hasClientRoleFilterOnCustomers">
          {Boolean(!defaultClientRole) && (
            <FormGroup columns={1}>
              <div className={styles.clientRoleContainer}>
                <Typography variant="label">Client Role</Typography>
                <Controller
                  name="client_role"
                  control={control}
                  rules={{required: true}}
                  render={({field}) => (
                    <Select
                      isSearchable={false}
                      menuPosition="fixed"
                      onChange={field.onChange}
                      options={CLIENT_ROLES_OPTIONS}
                      value={field.value}
                      placeholder={
                        <span className={styles.placeholder}>Client Role</span>
                      }
                      isClearable
                    />
                  )}
                />
              </div>
            </FormGroup>
          )}
        </FeatureGuard>

        <FormActionGroup className={styles.buttonWrap}>
          <Button
            onClick={handleSubmit(submithandler)}
            type="submit"
            loading={isLoading}
          >
            Save
          </Button>
        </FormActionGroup>
      </div>
    </Modal>
  );
}
export default NewCustomerModal;
